// Initialize Segment Analytics using traditional snippet
const SEGMENT_WRITE_KEY = process.env.REACT_APP_SEGMENT_WRITE_KEY;

/* eslint-disable no-unused-expressions, no-restricted-globals */
if (SEGMENT_WRITE_KEY && typeof window !== 'undefined') {
  // Segment analytics snippet
  window.analytics = window.analytics || [];
  if (!window.analytics.initialize) {
    if (window.analytics.invoked) {
      window.console && console.error && console.error("Segment snippet included twice.");
    } else {
      window.analytics.invoked = true;
      window.analytics.methods = ["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"];
      window.analytics.factory = function(e) {
        return function() {
          if (window.analytics.initialized) return window.analytics[e].apply(window.analytics, arguments);
          var n = Array.prototype.slice.call(arguments);
          if (["track","screen","alias","group","page","identify"].indexOf(e) > -1) {
            var c = document.querySelector("link[rel='canonical']");
            n.push({__t:"bpc",c:c&&c.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer});
          }
          n.unshift(e);
          window.analytics.push(n);
          return window.analytics;
        };
      };
      for (var n = 0; n < window.analytics.methods.length; n++) {
        var key = window.analytics.methods[n];
        window.analytics[key] = window.analytics.factory(key);
      }
      window.analytics.load = function(key, n) {
        var t = document.createElement("script");
        t.type = "text/javascript";
        t.async = true;
        t.setAttribute("data-global-segment-analytics-key", "analytics");
        t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";
        var r = document.getElementsByTagName("script")[0];
        r.parentNode.insertBefore(t, r);
        window.analytics._loadOptions = n;
      };
      window.analytics._writeKey = SEGMENT_WRITE_KEY;
      window.analytics.SNIPPET_VERSION = "5.2.0";
      window.analytics.load(SEGMENT_WRITE_KEY);
      window.analytics.page();
      console.log('Segment analytics initialized with key:', SEGMENT_WRITE_KEY.substring(0, 8) + '...');
    }
  }
} else {
  console.warn('REACT_APP_SEGMENT_WRITE_KEY not set - analytics disabled');
}
/* eslint-enable no-unused-expressions, no-restricted-globals */

export const analytics = typeof window !== 'undefined' ? window.analytics : null;

/**
 * Extract domain from URL
 * @param {string} url - The website URL
 * @returns {string} - The domain (e.g., "fueled.io")
 */
export function extractDomain(url) {
  if (!url) return '';
  try {
    // Add protocol if missing
    let urlWithProtocol = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      urlWithProtocol = 'https://' + url;
    }
    const parsed = new URL(urlWithProtocol);
    return parsed.hostname.replace(/^www\./, '');
  } catch (e) {
    // Fallback: try to extract domain from string
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s]+)/i);
    return match ? match[1] : url;
  }
}

/**
 * Get device type based on screen width
 * @returns {string} - "mobile", "tablet", or "desktop"
 */
export function getDeviceType() {
  const width = window.innerWidth;
  if (width < 464) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Read attribution params (UTM + click-IDs + affiliate) from the current URL,
 * with sessionStorage caching so first-touch attribution survives client-side
 * navigation within the same browsing session.
 *
 * Without caching: the visitor lands at /?utm_source=X but then the SPA
 * navigates to /status-checker/:email — the URL no longer has UTMs and
 * `getUtmParams()` returns empty, so events like CTA Clicked fire without
 * attribution.
 *
 * With caching: on first call we read the URL AND save to sessionStorage.
 * On subsequent calls (after route changes) we return the cached value.
 *
 * sessionStorage is scoped per tab — opening a new tab from email (e.g.
 * the "see progress" link) starts fresh, which is intentional.
 */
const UTM_STORAGE_KEY = '_dc_attribution_v1';
const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'msclkid', 'li_fat_id', 'twclid', 'ttclid', 'fpr'
];

export function getUtmParams() {
  if (typeof window === 'undefined') return {};
  try {
    // Read fresh from URL
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) fromUrl[k] = v;
    });

    // If URL has attribution, persist + return it (first-touch wins —
    // we don't overwrite existing stored attribution from later URL params)
    if (Object.keys(fromUrl).length > 0) {
      try {
        const existing = window.sessionStorage.getItem(UTM_STORAGE_KEY);
        if (!existing) {
          window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl));
        }
      } catch (e) { /* sessionStorage disabled — fine, just don't cache */ }
      return fromUrl;
    }

    // No URL attribution — fall back to whatever was stored on first touch
    try {
      const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch (e) { /* parse failure — return empty */ }

    return {};
  } catch (e) {
    return {};
  }
}

/**
 * Get common properties for all events
 * @param {Object} options - Additional properties
 * @param {string} options.email - User email
 * @param {string} options.websiteUrl - Website URL being processed
 * @returns {Object} - Common properties object
 */
export function getCommonProperties({ email = '', websiteUrl = '' } = {}) {
  return {
    email: email || '',
    domain: extractDomain(websiteUrl),
    website_url: websiteUrl || '',
    page_url: window.location.href,
    referrer: document.referrer || '',
    device_type: getDeviceType(),
    timestamp: new Date().toISOString(),
    // UTMs / click-IDs as top-level properties so Customer.io and other
    // Segment destinations see them as event attributes.
    ...getUtmParams()
  };
}

/**
 * Track an event with enriched properties
 * @param {string} eventName - Name of the event
 * @param {Object} properties - Event-specific properties
 * @param {Object} commonProps - Common properties (email, websiteUrl)
 */
export function trackEvent(eventName, properties = {}, commonProps = {}) {
  if (!analytics) {
    console.warn('Analytics not loaded');
    return;
  }

  const enrichedProperties = {
    ...getCommonProperties(commonProps),
    ...properties
  };

  analytics.track(eventName, enrichedProperties);
}

/**
 * Identify a user with traits
 * @param {string} email - User email (used as user ID)
 * @param {Object} traits - User traits
 */
export function identifyUser(email, traits = {}) {
  if (!analytics || !email) {
    return;
  }

  // Include UTMs as user traits so Customer.io sets them as profile
  // attributes (enables segmentation like "users from utm_source=X").
  analytics.identify(email, {
    email,
    ...getUtmParams(),
    ...traits
  });
}
